import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { GygUserType, GygUsersType } from 'src/types/apps/gygUserTypes'
import { Redux } from 'src/store'

type GygUserStateType = {
  gygUsers: GygUsersType[]
  gygUser: GygUserType | null
  pending: boolean
  getUserPending: boolean
  getUserError: any
  updateUserPending: boolean
  errors: any // todo
  success: boolean
}

interface UserSearchResponse {
  data: GygUsersType[]
}

interface UserResponse {
  data: GygUserType
}

interface UserResetPasswordResponse {
  message: string
}

interface UserResendEmailVerificationResponse {
  message: string
}

interface SendForgotPasswordResponse {
  message: string
}

interface UserUpdateError {
  error: string
  errors: any[]
  statusCode: number
}
interface UserDeleteError {
  error: string
  errors: any[]
  statusCode: number
}

interface UserUpdatePayload {
  firstName: string
  lastName: string
  dateofbirth: string | null
  mobile: string
  email: string
  loyalty?: {
    id: string | undefined
    cardNumber: string | undefined
  }
}

interface SendPasswordPayload {
  email: string
}

interface UserBlockPayload {
  blockReason: string
}

interface UserResetPasswordPayload {
  newPassword: string
  appsmithUserId: string
}

interface UserUpdateThunkPayload {
  id: string
  data: UserUpdatePayload
}

interface UserBlockThunkPayload {
  id: string
  data: UserBlockPayload
}

interface UserResetPasswordThunkPayload {
  id: string
  data: UserResetPasswordPayload
}

interface CreateBrazeAccountPayload {
  userId: string
  userType: string
  branchId: string
}

export const searchUser = createAsyncThunk<UserSearchResponse, string, Redux>(
  'gygUsers/searchUser',
  async (query: string, { extra, rejectWithValue }) => {
    try {
      const response = await extra.httpClient(`users/search?query=${query}`)

      return response.data
    } catch (error: any) {
      if (error.response) {
        return rejectWithValue(error.response.data)
      }

      return rejectWithValue(error)
    }
  }
)

export const getUser = createAsyncThunk<UserResponse, string, Redux>(
  'gygUsers/getUser',
  async (userId: string, { extra, rejectWithValue }) => {
    try {
      const response = await extra.httpClient(`users/${userId}`)

      return response.data
    } catch (error: any) {
      if (error.response) {
        return rejectWithValue(error.response.data)
      }

      return rejectWithValue(error)
    }
  }
)

export const updateUser = createAsyncThunk<UserResponse | UserUpdateError, UserUpdateThunkPayload, Redux>(
  'gygUsers/updateUser',
  async ({ id, data }, { extra, rejectWithValue }) => {
    try {
      const response = await extra.httpClient(`users/${id}`, { method: 'PATCH', data })

      return response.data
    } catch (error: any) {
      if (error.response) {
        return rejectWithValue(error.response.data)
      }
    }
  }
)

export const blockUser = createAsyncThunk<UserResponse | UserUpdateError, UserBlockThunkPayload, Redux>(
  'gygUsers/blockUser',
  async ({ id, data }, { extra, rejectWithValue }) => {
    try {
      const response = await extra.httpClient(`users/${id}/block`, { method: 'PATCH', data })

      return response.data
    } catch (error: any) {
      if (error.response) {
        return rejectWithValue(error.response.data)
      }
    }
  }
)

export const unblockUser = createAsyncThunk<UserResponse | UserUpdateError, string, Redux>(
  'gygUsers/unblockUser',
  async (id, { extra, rejectWithValue }) => {
    try {
      const response = await extra.httpClient(`users/${id}/unblock`, { method: 'PATCH', data: {} })

      return response.data
    } catch (error: any) {
      if (error.response) {
        return rejectWithValue(error.response.data)
      }
    }
  }
)

export const resetPassword = createAsyncThunk<UserResetPasswordResponse, UserResetPasswordThunkPayload, Redux>(
  'gygUsers/resetPassword',
  async ({ id, data }, { extra, rejectWithValue }) => {
    try {
      const response = await extra.gygInternalApiClient(`/auth/resetpassword/${id}`, { method: 'PUT', data })
      const responseData = response.data
      if (responseData.body?.message !== 'SUCCESS') {
        // todo(jhudiel) - handle if response is not SUCCESS
        // return rejectWithValue()
      } else {
        return response.data
      }
    } catch (error: any) {
      if (error.response) {
        return rejectWithValue(error.response.data)
      }
    }
  }
)

export const resendEmailVerification = createAsyncThunk<UserResendEmailVerificationResponse, string, Redux>(
  'gygUsers/resendEmailVerification',
  async (userId, { extra, rejectWithValue }) => {
    try {
      const response = await extra.gygInternalApiClient(`/auth/verify/trigger/email/resend/${userId}`)

      const responseData = response.data
      if (responseData.body?.message !== 'SUCCESS') {
        // todo(jhudiel) - handle if response is not SUCCESS
        // return rejectWithValue()
      } else {
        return response.data
      }
    } catch (error: any) {
      if (error.response) {
        return rejectWithValue(error.response.data)
      }
    }
  }
)

export const sendForgotPassword = createAsyncThunk<SendForgotPasswordResponse, SendPasswordPayload, Redux>(
  'gygUsers/sendForgotPassword',
  async (payload, { extra, rejectWithValue }) => {
    try {
      const response = await extra.gygExternalApiClient(`/auth/forgotpassword`, {
        method: 'POST',
        data: {
          emailAddress: payload.email
        }
      })

      return response.data
    } catch (error: any) {
      if (error.response) {
        return rejectWithValue(error.response.data)
      }
    }
  }
)

export const createBrazeAccount = createAsyncThunk<UserResponse, CreateBrazeAccountPayload, Redux>(
  'gygUsers/createBrazeAccount',
  async (payload, { extra, rejectWithValue }) => {
    try {
      const response = await extra.httpClient(`user-external-identities/braze/create`, {
        method: 'POST',
        data: payload
      })

      return response.data
    } catch (error: any) {
      if (error.response) {
        return rejectWithValue(error.response.data)
      }
    }
  }
)

// ** Delete User
export const deleteUser = createAsyncThunk<UserResponse | UserDeleteError, string, Redux>(
  'gygUsers/deleteUser',
  async (userId: string | number, { extra, rejectWithValue }) => {
    try {
      const response = await extra.httpClient(`users/${userId}`, { method: 'DELETE' })

      return response.data
    } catch (error: any) {
      if (error.response) {
        return rejectWithValue(error.response.data)
      }
    }
  }
)

const initialState: GygUserStateType = {
  gygUsers: [],
  gygUser: null,
  errors: {},
  success: false,
  pending: false,
  getUserPending: false,
  getUserError: null,
  updateUserPending: false
}

export const gygUsersSlice = createSlice({
  name: 'gygUsers',
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder

      // User search
      .addCase(searchUser.fulfilled, (state, action) => {
        state.pending = false
        state.success = false
        state.gygUsers = action.payload.data
      })
      .addCase(searchUser.pending, state => {
        state.pending = true
        state.success = false
      })
      .addCase(searchUser.rejected, (state, action) => {
        state.pending = false
        state.success = false
        state.errors = action.payload
      })

      // User get
      .addCase(getUser.fulfilled, (state, action) => {
        state.getUserPending = false
        state.getUserError = null
        state.success = false
        state.gygUser = action.payload.data
      })
      .addCase(getUser.pending, state => {
        state.getUserPending = true
        state.getUserError = null
      })
      .addCase(getUser.rejected, (state, action) => {
        state.getUserPending = false
        state.getUserError = action.payload
        state.gygUser = null
      })

      // User update
      .addCase(updateUser.fulfilled, (state, action) => {
        state.updateUserPending = false
        state.success = true
        state.gygUser = (action.payload as UserResponse).data
        state.errors = null
      })
      .addCase(updateUser.pending, state => {
        state.updateUserPending = true
      })
      .addCase(updateUser.rejected, (state, action) => {
        state.errors = (action.payload as UserUpdateError).errors
        state.updateUserPending = false
        state.success = false
      })

      // User block
      .addCase(blockUser.fulfilled, (state, action) => {
        state.updateUserPending = false
        state.success = true
        state.gygUser = (action.payload as UserResponse).data
        state.errors = null
      })
      .addCase(blockUser.pending, state => {
        state.updateUserPending = true
      })
      .addCase(blockUser.rejected, (state, action) => {
        state.errors = (action.payload as UserUpdateError).errors
        state.updateUserPending = false
        state.success = false
      })

      // User block
      .addCase(unblockUser.fulfilled, (state, action) => {
        state.updateUserPending = false
        state.success = true
        state.gygUser = (action.payload as UserResponse).data
        state.errors = null
      })
      .addCase(unblockUser.pending, state => {
        state.updateUserPending = true
      })
      .addCase(unblockUser.rejected, (state, action) => {
        let errors
        if ((action.payload as UserUpdateError).errors) {
          errors = (action.payload as UserUpdateError).errors
        } else {
          errors = action.payload
        }

        state.errors = errors
        state.success = false
        state.updateUserPending = false
      })

      // Reset password
      .addCase(resetPassword.fulfilled, state => {
        state.updateUserPending = false
        state.success = true
      })
      .addCase(resetPassword.pending, state => {
        state.updateUserPending = true
      })
      .addCase(resetPassword.rejected, (state, action) => {
        // todo(jhudiel) - handle errors
        state.errors = (action.payload as UserDeleteError).errors
        state.updateUserPending = false
        state.success = false
      })

      // Delete User
      .addCase(deleteUser.fulfilled, (state, action) => {
        state.updateUserPending = false
        state.success = true
        state.gygUser = (action.payload as UserResponse).data
        state.errors = null
      })
      .addCase(deleteUser.pending, state => {
        state.updateUserPending = true
      })
      .addCase(deleteUser.rejected, (state, action) => {
        state.errors = (action.payload as UserDeleteError).errors
        state.success = false
        state.updateUserPending = false
      })

      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      .addCase(resendEmailVerification.fulfilled, (state, action) => {
        state.updateUserPending = false
        state.success = true
        state.errors = null
      })
      .addCase(resendEmailVerification.pending, state => {
        state.updateUserPending = true
      })

      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      .addCase(resendEmailVerification.rejected, (state, action) => {
        // todo(jhudiel)
        state.errors = (action.payload as UserDeleteError).errors
        state.success = false
        state.updateUserPending = false
      })

      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      .addCase(sendForgotPassword.fulfilled, (state, action) => {
        state.updateUserPending = false
        state.success = true
        state.errors = null
      })
      .addCase(sendForgotPassword.pending, state => {
        state.updateUserPending = true
      })

      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      .addCase(sendForgotPassword.rejected, (state, action) => {
        // todo(jhudiel)
        state.errors = (action.payload as UserDeleteError).errors
        state.success = false
        state.updateUserPending = false
      })

      // Create Braze Account
      .addCase(createBrazeAccount.fulfilled, (state, action) => {
        state.updateUserPending = false
        state.success = true
        state.gygUser = action.payload.data
        state.errors = null
      })
      .addCase(createBrazeAccount.pending, state => {
        state.updateUserPending = true
      })
      .addCase(createBrazeAccount.rejected, (state, action) => {
        state.errors = (action.payload as UserUpdateError).errors
        state.updateUserPending = false
        state.success = false
      })
  }
})

export default gygUsersSlice.reducer
